import { ListChecks, LoaderCircle, LocateFixed, Lock, MapPin, Search, Send, SlidersHorizontal, Sparkles, Trophy, X } from 'lucide-react'
import { offeringLines } from '../../utils/offeringLines'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiErrorMessage, isAbortError } from '../../api/client'
import { connectionsApi, discoveryApi, profileApi, servicesApi } from '../../api/endpoints'
import { useAuth } from '../../auth/AuthContext'
import { ErrorMessage } from '../../components/ErrorMessage'
import { HiddenReasons } from '../../components/map/HiddenReasons'
import { ProviderMap, type MapDot } from '../../components/map/ProviderMap'
import { ResultCountSummary } from '../../components/ResultCountSummary'
import { Stars } from '../../components/profile/RecentRatings'
import { ReportBlockActions } from '../../components/trust/ReportBlockActions'
import { useConnectionGate } from '../../hooks/useConnectionGate'
import { useContactConsent } from '../../hooks/useContactConsent'
import { useFocusedFromNavigation } from '../../hooks/useFocusedFromNavigation'
import { useMapCentre } from '../../hooks/useMapCentre'
import { NEED_CATEGORY_LABELS, summarizeJson } from '../../utils/taxonomy'
import type {
  MapLearnerResponse,
  MapLearnerSearchResponse,
  NeedCategory,
  ParticipantCardResponse,
  ProviderServiceResponse,
} from '../../types/api'

const RADII: { label: string; km: number }[] = [
  { label: '2 km', km: 2 },
  { label: '5 km', km: 5 },
  { label: '10 km', km: 10 },
  { label: '25 km', km: 25 },
  { label: '50 km', km: 50 },
]
const RATINGS: { label: string; value: number | null }[] = [
  { label: 'Any', value: null },
  { label: '3 ★', value: 3 },
  { label: '4 ★', value: 4 },
  { label: '4.5 ★', value: 4.5 },
  { label: '5 ★', value: 5 },
]

function chipClass(active: boolean) {
  return `chip ${active ? 'chip-active' : ''}`
}

/**
 * The provider's map. Learners are anonymous dots: the API returns no name until both sides have
 * accepted, so there is nothing here to hide in the client.
 */
export function TutorMapView() {
  const { profile } = useAuth()
  const [radiusKm, setRadiusKm] = useState(5)
  const [minRating, setMinRating] = useState<number | null>(null)
  const [query, setQuery] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  // Use the details you and the learner both filled in (mode, budget, target year, ...). A detail only
  // one of you stated never excludes anyone. Off until the provider turns it on: the map opens on
  // every learner nearby, and matching on details is a narrowing they choose.
  const [matchAttributes, setMatchAttributes] = useState(false)
  // Which of the provider's own services to show the map for - the mirror of the learner map's
  // "My needs" filter. Unset shows every service at once, same as before this existed.
  const [services, setServices] = useState<ProviderServiceResponse[]>([])
  const [serviceCategory, setServiceCategory] = useState<NeedCategory | null>(null)

  useEffect(() => {
    servicesApi.listOwn().then(setServices).catch(() => null)
  }, [])

  const [result, setResult] = useState<MapLearnerSearchResponse | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [openDetail, setOpenDetail] = useState(false)
  // Same rule as the learner map: open on a live GPS fix when the browser will give one, falling
  // back to their coordinates, or their pincode resolved once.
  const {
    centre: coords,
    locating,
    locatingFor,
    source: coordsSource,
    requestCurrentLocation,
    requestSavedLocation,
    locationError,
  } = useMapCentre()
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [connecting, setConnecting] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  // A provider is never the gated party here, but they still must not be offered Connect on a
  // learner they already have a request with.
  const gate = useConnectionGate()
  const consent = useContactConsent()

  useEffect(() => {
    const controller = new AbortController()
    const timer = setTimeout(() => {
      setLoading(true)
      discoveryApi
        .learners(
          {
            lat: coords?.lat,
            lng: coords?.lng,
            radiusKm,
            minRating: minRating ?? undefined,
            q: query.trim() || undefined,
            category: serviceCategory ?? undefined,
            matchAttributes,
          },
          controller.signal,
        )
        .then((res) => {
          setResult(res)
          setError(null)
          setLoading(false)
        })
        .catch((err) => {
          if (isAbortError(err)) return
          setError(apiErrorMessage(err, 'Could not load the map'))
          setLoading(false)
        })
    }, query ? 350 : 0)

    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [coords, radiusKm, minRating, query, serviceCategory, matchAttributes])

  // Arriving from a profile recommendation's Connect: open that learner's card straight away.
  useFocusedFromNavigation(result?.learners, (id) => {
    setSelectedId(id)
    setOpenDetail(true)
  })

  async function connect(learner: MapLearnerResponse) {
    setError(null)
    // Contact details are shared once they accept - the sender agrees to that up front.
    const agreed = await consent.ask('initiate', 'learner')
    if (agreed === false) return
    setConnecting(true)
    try {
      await connectionsApi.create({
        receiverParticipantId: learner.id,
        aboutItems: learner.subjects,
        contactConsent: agreed,
      })
      gate.refresh()
      setNotice(
        'Request sent. If the learner is under 18 it goes to their parent first; you will see '
          + 'their details once it is accepted.',
      )
      setOpenDetail(false)
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not send the connection request'))
    } finally {
      setConnecting(false)
    }
  }

  // Distinct, in the order the provider first added them - two services in the same category would
  // otherwise show the same chip twice.
  const serviceCategories = [...new Set(services.map((s) => s.category))]
  // Named only when it is unambiguous - the one category filtered on, or the one category every
  // service shares - never a guess at which of several the provider means.
  const categoryLabel = serviceCategory
    ? NEED_CATEGORY_LABELS[serviceCategory]
    : serviceCategories.length === 1
      ? NEED_CATEGORY_LABELS[serviceCategories[0]]
      : null
  const learners = result?.learners ?? []
  /**
   * How many learner rows the list renders before it stops.
   *
   * <p>Unlike the student-facing map, which lists every provider it found, this side can be
   * enormous: the learner side of the network is the whole student body, and 25 km around Delhi
   * is over three thousand of them. Rendering that many buttons makes the panel janky to scroll
   * for a tutor who was only ever going to read the top of it. Two hundred covers a city like
   * Vizag several times over, and the count below says plainly how many were left out and what
   * to do about it.
   */
  const MAX_LISTED = 200
  const listed = learners.slice(0, MAX_LISTED)
  // Memoized on the actual coordinates, not recreated fresh every render - see the identical
  // comment in MapPage.tsx, the learner-side mirror of this page.
  const centre = useMemo(
    () =>
      result && (result.centreLat !== 0 || result.centreLng !== 0)
        ? { lat: result.centreLat, lng: result.centreLng }
        : null,
    [result?.centreLat, result?.centreLng],
  )
  const selected = learners.find((l) => l.id === selectedId) ?? null
  const wider = RADII.find((r) => r.km > radiusKm) ?? null

  const dots: MapDot[] = learners.map((l) => ({
    id: l.id,
    lat: l.lat,
    lng: l.lng,
    name: l.connected ? l.fullName ?? 'Learner' : 'Learner',
    distanceKm: l.distanceKm,
    subtitles: offeringLines(l.offerings, l.category, l.subjects),
    tone: 'learner',
    averageRating: l.averageRating,
    ratingCount: l.ratingCount,
    fourCScored: l.fourCScored,
    aiCertified: l.aiCertified,
    blueRing: l.blueRing,
  }))

  return (
    <div className="space-y-4">
      {consent.dialog}
      <div>
        <h1 className="flex items-center gap-2 text-xl font-bold text-ink-900">
          <MapPin size={18} className="text-sun-500" /> YellowDots Map
        </h1>
        <p className="text-sm text-ink-500">
          Learners near you looking for{' '}
          {categoryLabel ? (
            <strong className="text-ink-900">{categoryLabel}</strong>
          ) : (
            'the category and subject you teach'
          )}
          . Names are shared once you are connected.
          {!loading && result && (
            <>
              <ResultCountSummary
                shown={learners.length}
                totalInRadius={result.totalInRadius}
                networkTotal={result.networkTotal}
                moreOnNetwork={result.moreOnNetwork}
                radiusKm={result.radiusKm}
                label="learners"
              />
              {/* The city/state on the profile belong to the saved location, not wherever a live
                  GPS fix happens to be - shown only when that is actually what the map is using,
                  so it never names a place the results and the "You" dot are not really near. */}
              {coordsSource === 'saved' && profile?.city && profile?.state && (
                <>
                  {' '}in <strong className="text-ink-900">{profile.city}, {profile.state}</strong>
                </>
              )}
              {coordsSource && (coordsSource === 'gps' ? ', based on your live location' : ', based on your saved location')}
              .
              {wider && (
                <button
                  onClick={() => setRadiusKm(wider.km)}
                  className="ml-1 font-semibold text-amber-700 underline-offset-4 hover:underline"
                >
                  Widen to {wider.label}
                </button>
              )}
            </>
          )}
        </p>
        {loading && (
          <div className="chip chip-active mt-2 w-fit text-sm font-semibold">
            <LoaderCircle size={16} className="animate-spin" /> Searching for learners near you...
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2 sm:flex-row">
        <label className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="input !pl-9"
            placeholder="Search subject"
            aria-label="Search subject"
          />
        </label>
        <button onClick={() => setShowFilters((s) => !s)} className="btn-ghost shrink-0">
          <SlidersHorizontal size={15} /> Filters
        </button>
        <button
          onClick={() => setMatchAttributes((m) => !m)}
          className={matchAttributes ? 'chip chip-active shrink-0' : 'btn-ghost shrink-0'}
          title="Only show learners whose details agree with what you offer on everything you both filled in"
        >
          <ListChecks size={15} /> Match my details
        </button>
        {/* Two buttons, not a single toggle, so which one the map is actually using is always
            visible at a glance - the selected one is highlighted the same way a radius or rating
            chip is below. */}
        <button
          onClick={requestCurrentLocation}
          disabled={locating}
          className={`${chipClass(coordsSource === 'gps')} shrink-0 disabled:cursor-wait disabled:opacity-70`}
          title="Search near where you are right now"
        >
          <LocateFixed size={15} /> {locatingFor === 'gps' ? 'Locating...' : 'Live location'}
        </button>
        <button
          onClick={() => requestSavedLocation()}
          disabled={locating}
          className={`${chipClass(coordsSource === 'saved')} shrink-0 disabled:cursor-wait disabled:opacity-70`}
          title="Search near your saved address"
        >
          <MapPin size={15} /> {locatingFor === 'saved' ? 'Locating...' : 'Saved location'}
        </button>
      </div>

      {matchAttributes && result && result.hiddenByAttributes > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800">
          <span className="flex flex-col gap-1.5">
            <span className="flex items-center gap-2">
              <ListChecks size={15} />
              {result.hiddenByAttributes} nearby learner{result.hiddenByAttributes === 1 ? ' is' : 's are'} hidden
              because their details differ from what you offer:
            </span>
            <HiddenReasons reasons={result.hiddenReasons} who="they asked for" />
          </span>
          <button
            onClick={() => setMatchAttributes(false)}
            className="shrink-0 font-semibold underline-offset-4 hover:underline"
          >
            Show them
          </button>
        </div>
      )}


      {showFilters && (
        <div className="card-pad">
          <div className="mb-3 text-[10px] font-bold uppercase tracking-[0.14em] text-ink-400">Filters</div>
          <div className="flex flex-col gap-4 md:flex-row md:flex-wrap md:items-start md:gap-x-10 md:gap-y-4">
            {/* Only shown once there is something to narrow - a single service gets the same map
                either way, and the choice would just be clutter. */}
            {serviceCategories.length > 1 && (
              <div>
                <span className="label">My services</span>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => setServiceCategory(null)} className={chipClass(serviceCategory === null)}>
                    All
                  </button>
                  {serviceCategories.map((c) => (
                    <button
                      key={c}
                      onClick={() => setServiceCategory(c)}
                      className={chipClass(serviceCategory === c)}
                    >
                      {NEED_CATEGORY_LABELS[c]}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <span className="label">Minimum rating</span>
              <div className="flex flex-wrap gap-2">
                {RATINGS.map((r) => (
                  <button key={r.label} onClick={() => setMinRating(r.value)} className={chipClass(minRating === r.value)}>
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <span className="label">Search radius</span>
              <div className="flex flex-wrap gap-2">
                {RADII.map((r) => (
                  <button
                    key={r.label}
                    onClick={() => setRadiusKm(r.km)}
                    className={chipClass(radiusKm === r.km)}
                    title="As the crow flies - the distance shown per listing is by road, and can be a little more"
                  >
                    {r.label}
                  </button>
                ))}
              </div>
              {/* A hover title alone never reaches a touch screen - this is the same note, always
                  on, so the radius-vs-shown-distance difference (straight line vs by road) isn't
                  mobile-invisible. */}
              <p className="mt-1.5 text-xs text-ink-400">
                Radius is a straight line; the distance on each listing is by road, so it can be a
                little more.
              </p>
            </div>
          </div>
        </div>
      )}

      <ErrorMessage message={error ?? locationError} />
      {notice && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {notice}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="card overflow-hidden lg:col-span-2">
          <ProviderMap
            centre={centre}
            radiusKm={result?.radiusKm ?? radiusKm}
            dots={dots}
            selectedId={selectedId}
            onSelect={(id) => {
              setSelectedId(id)
              setOpenDetail(true)
            }}
            countLabel={`${learners.length} listing${learners.length === 1 ? '' : 's'}`}
          />
        </div>

        <div className="space-y-4">
          <div className="card-pad">
            {selected ? (
              <LearnerDetail
                learner={selected}
                onConnect={() => setOpenDetail(true)}
                existingLabel={gate.labelFor(selected.id)}
              />
            ) : (
              <>
                <p className="text-sm text-ink-500">Tap a dot to see details.</p>
                <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-500">
                  <span><span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-ink-900 align-middle" />You</span>
                  <span><span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-sun-500 align-middle" />Learner</span>
                </p>
              </>
            )}
          </div>

          <div className="card-pad">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[10px] font-bold uppercase tracking-wide text-ink-400">Nearest</span>
              {learners.length > 0 && (
                <span className="text-[10px] font-semibold text-ink-400">
                  {learners.length} {learners.length === 1 ? 'listing' : 'listings'}
                </span>
              )}
            </div>
            {loading ? (
              <p className="mt-2 flex items-center gap-1.5 text-sm text-ink-500">
                <LoaderCircle size={14} className="animate-spin" /> Searching...
              </p>
            ) : !centre ? (
              <p className="mt-2 text-sm text-ink-500">Set your location to see who is near you.</p>
            ) : learners.length === 0 ? (
              <p className="mt-2 text-sm text-ink-500">
                No learners looking for your category and subject are nearby yet.{' '}
                <button
                  onClick={() => { setMinRating(null); setQuery(''); setServiceCategory(null) }}
                  className="font-semibold text-amber-700 underline-offset-4 hover:underline"
                >
                  Clear filters
                </button>{' '}
                or{' '}
                <Link to="/services" className="font-semibold text-amber-700 underline-offset-4 hover:underline">
                  check what's on your profile
                </Link>
                .
              </p>
            ) : (
              <ul className="mt-2 max-h-[26rem] space-y-2 overflow-y-auto pr-1">
                {listed.map((l) => (
                  <li key={l.id}>
                    <button
                      onClick={() => { setSelectedId(l.id); setOpenDetail(true) }}
                      className={`flex w-full items-baseline justify-between gap-2 rounded-xl border px-3 py-2 text-left transition ${
                        l.id === selectedId ? 'border-sun-400 bg-sun-400/10' : 'border-ink-200 hover:bg-ink-50'
                      }`}
                    >
                      <span className="min-w-0 break-words text-sm font-semibold text-ink-900">
                        {l.connected ? l.fullName : 'Learner'}
                        {/* Full subject list and mode, not just the first subject - many rows
                            share a category, a pincode-derived coordinate and so a rounded
                            distance too (see PincodeGeocoder), and with the name hidden until
                            connected this is the only thing that can tell two rows apart. */}
                        {(l.category || l.subjects.length > 0 || l.mode) && (
                          <span className="font-normal text-ink-500">
                            {' '}· {[l.category, l.subjects.join(', ') || null, l.mode].filter(Boolean).join(' · ')}
                          </span>
                        )}
                      </span>
                      <span className="shrink-0 text-xs text-ink-500">{l.distanceKm} km</span>
                    </button>
                  </li>
                ))}
                {learners.length > listed.length && (
                  <li className="px-3 pt-1 text-xs text-ink-500">
                    +{learners.length - listed.length} more within {radiusKm} km. Narrow the radius
                    to see them all.
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>
      </div>

      {selected && openDetail && (
        <LearnerDialog
          learner={selected}
          connecting={connecting}
          onClose={() => setOpenDetail(false)}
          onConnect={() => connect(selected)}
          existingLabel={gate.labelFor(selected.id)}
          error={error}
        />
      )}
    </div>
  )
}

function LearnerDetail({
  learner,
  onConnect,
  /** "Connected" / "Request sent" / "Waiting for parent approval", when one already exists. */
  existingLabel,
}: {
  learner: MapLearnerResponse
  onConnect: () => void
  existingLabel?: string | null
}) {
  return (
    <div>
      <div className="font-semibold text-ink-900">{learner.connected ? learner.fullName : 'Learner'}</div>
      <div className="text-xs text-ink-500">~{learner.distanceKm} km away</div>

      {learner.averageRating != null && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-ink-500">
          <Stars rating={learner.averageRating} size={12} /> {learner.averageRating} ({learner.ratingCount})
        </div>
      )}

      {learner.category && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          <span className="pill bg-sun-400/20 text-amber-800">{learner.category}</span>
        </div>
      )}
      {learner.subjects.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {learner.subjects.map((s) => (
            <span key={s} className="pill bg-ink-100 text-ink-700">{s}</span>
          ))}
        </div>
      )}

      <dl className="mt-3 space-y-1 text-sm">
        {learner.mode && (
          <div className="flex justify-between gap-3">
            <dt className="text-ink-500">Mode</dt>
            <dd className="text-right font-semibold text-ink-900">{learner.mode}</dd>
          </div>
        )}
        {learner.budgetBand && (
          <div className="flex justify-between gap-3">
            <dt className="text-ink-500">Budget</dt>
            <dd className="text-right font-semibold text-ink-900">{learner.budgetBand}</dd>
          </div>
        )}
      </dl>

      {/* Same rule as the provider card on /map: once a connection exists the button stops
          offering to make one, and says so where the caveat used to be. The label comes from the
          connection gate rather than learner.connected, because "Request sent" and "Waiting for
          parent approval" are also states in which Connect must not be offered again - and that
          flag only knows about the accepted one. */}
      <button onClick={onConnect} className="btn-sun mt-3 w-full">
        {existingLabel ? 'View profile' : 'View profile & connect'}
      </button>
      {existingLabel ? (
        <p className="mt-2 text-center">
          <span className="rounded-full bg-ink-100 px-3 py-1 text-xs font-semibold text-ink-600">
            {existingLabel}
          </span>
        </p>
      ) : (
        !learner.connected && (
          <p className="mt-2 text-center text-xs text-ink-400">
            Only needs and budget are shown until you are connected.
          </p>
        )
      )}
    </div>
  )
}

function LearnerDialog({
  learner,
  connecting,
  onClose,
  onConnect,
  existingLabel,
  error,
}: {
  learner: MapLearnerResponse
  connecting: boolean
  onClose: () => void
  onConnect: () => void
  /** Set when a connection already exists with this learner - the button is replaced by it. */
  existingLabel?: string | null
  /** Why the last Connect failed. Shown here because the page's own message is behind this dialog. */
  error?: string | null
}) {
  // The mirror of ProviderDialog's own card fetch: every need this learner has saved, not only
  // the one that matched this search - same pre-connect rules as the card everywhere else (no
  // name, class, school or parent details until a connection is accepted).
  const [card, setCard] = useState<ParticipantCardResponse | null>(null)
  useEffect(() => {
    let cancelled = false
    profileApi.getCard(learner.id).then((c) => {
      if (!cancelled) setCard(c)
    }).catch(() => null)
    return () => {
      cancelled = true
    }
  }, [learner.id])
  const needs = card?.needs ?? []

  return (
    <div className="fixed inset-0 z-[1100] grid place-items-center overflow-y-auto bg-ink-900/40 p-4">
      <div className="card-pad max-h-[90vh] w-full max-w-md overflow-y-auto bg-white">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-sun-400/20 text-amber-700">
              <Lock size={16} />
            </span>
            <div>
              <div className="font-bold text-ink-900">{learner.connected ? learner.fullName : 'Learner'}</div>
              <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-ink-500">
                <span>~{learner.distanceKm} km</span>
                {/* Same two credentials the map pin's own tooltip already shows (see ProviderMap) -
                    missing here before. Neither identifies them, so shown whether or not connected. */}
                {learner.fourCScored && (
                  <span className="inline-flex items-center gap-0.5 rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700">
                    <Trophy size={10} /> 4C Assessment taken
                  </span>
                )}
                {learner.aiCertified && (
                  <span className="inline-flex items-center gap-0.5 rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700">
                    <Sparkles size={10} /> AI certified · Violet Dots
                  </span>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-ink-400 hover:text-ink-700">
            <X size={18} />
          </button>
        </div>

        {!learner.connected && (
          <p className="mt-3 flex gap-2 rounded-xl bg-ink-50 px-3 py-2 text-xs text-ink-600">
            <Lock size={13} className="mt-0.5 shrink-0" />
            Only this learner&apos;s needs and budget are shown. Their name, class, school and parent
            or guardian details are shared once you both accept a connection.
          </p>
        )}

        <div className="mt-3">
          <div className="text-[10px] font-bold uppercase tracking-wide text-ink-400">Wants to learn</div>
          {needs.length === 0 ? (
            card ? (
              learner.category ? (
                // The card loaded with no need rows at all (an old import, pre-dating per-need
                // storage) - fall back to the flat columns the search result itself carried.
                <>
                  <p className="mt-1 text-sm font-semibold text-ink-900">{learner.category}</p>
                  {learner.subjects.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {learner.subjects.map((s) => (
                        <span key={s} className="pill bg-ink-100 text-ink-700">{s}</span>
                      ))}
                    </div>
                  )}
                  <p className="mt-2 text-sm text-ink-600">
                    {[learner.mode, learner.timing.join(', ') || null, learner.budgetBand]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </>
              ) : (
                <p className="mt-1 text-sm text-ink-500">They have not listed a category yet.</p>
              )
            ) : (
              <p className="mt-1 text-sm text-ink-500">Loading...</p>
            )
          ) : (
            // Every need, not only the one that matched this search - the same "across every need
            // they stated" rule the search itself already applies, shown in full here.
            <ul className="mt-1.5 space-y-3">
              {needs.map((n) => {
                const items = summarizeJson(n.items)
                const extra = [
                  n.level,
                  n.mode,
                  summarizeJson(n.timing),
                  n.budgetBand,
                  n.preferredTutorGender && n.preferredTutorGender !== 'No Preference'
                    ? `Prefers a ${n.preferredTutorGender.toLowerCase()} tutor`
                    : null,
                  summarizeJson(n.detail),
                ].filter(Boolean)
                return (
                  <li key={n.id}>
                    <p className="text-sm font-semibold text-ink-900">
                      {NEED_CATEGORY_LABELS[n.category] ?? n.category}
                      {n.isPrimary && (
                        <span className="ml-1.5 pill bg-sun-400/20 text-[10px] text-amber-800">Primary</span>
                      )}
                    </p>
                    {items && (
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {items.split(', ').map((s) => (
                          <span key={s} className="pill bg-ink-100 text-ink-700">{s}</span>
                        ))}
                      </div>
                    )}
                    {extra.length > 0 && (
                      <p className="mt-1 text-sm text-ink-600">{extra.join(' · ')}</p>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        {error && (
          <div className="mt-3">
            <ErrorMessage message={error} />
          </div>
        )}

        {/* Low-key: a safety tool, not a primary action. A learner not yet connected is
            "this learner" - their name is not shared until both accept. */}
        <div className="mt-3 flex justify-end">
          <ReportBlockActions
            participantId={learner.id}
            name={learner.connected && learner.fullName ? learner.fullName : 'this learner'}
            onBlocked={onClose}
          />
        </div>

        <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-ink-400">
            {/* A minor learner's request goes to their parent first, so say so before the tutor
                presses the button - otherwise "Request sent" followed by silence looks broken. */}
            Contact details are shared only after a request is accepted. If the learner is under
            18, their parent approves first.
          </p>
          {existingLabel ? (
            <span className="shrink-0 rounded-full bg-ink-100 px-3 py-1.5 text-sm font-semibold text-ink-600">
              {existingLabel}
            </span>
          ) : (
            <button onClick={onConnect} disabled={connecting} className="btn-sun shrink-0">
              <Send size={15} /> {connecting ? 'Sending...' : 'Connect'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
